import React, { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Link as LinkIcon, ShieldCheck, ShieldOff, Sparkles } from "lucide-react";

export default function BuilderURLPanel({ onImported }) {
  const [url, setUrl] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState(null);

  const doPreview = async () => {
    if (!url.trim()) { toast.error("Paste a builder-site URL first."); return; }
    setLoading(true); setPreview(null); setError(null); setConsent(false);
    try {
      const { data } = await api.post("/listings/builder-url/preview", { url: url.trim() });
      setPreview(data);
      toast.success("Compliance-gated fetch succeeded. Review the extracted variants below.");
    } catch (e) {
      const detail = e?.response?.data?.detail || "Preview failed.";
      const code = e?.response?.status;
      setError({ code, detail });
      toast.error(code === 451 ? "Compliance gate blocked this URL." : detail);
    } finally {
      setLoading(false);
    }
  };

  const doCommit = async () => {
    if (!preview) return;
    if (!consent) { toast.error("Tick the consent box before importing."); return; }
    setCommitting(true);
    try {
      const ex = preview.extracted || {};
      const items = (preview.variants || []).map((v) => ({
        bhk: v.bhk,
        unit_variant: v.unit_variant,
        price_lakhs: v.price_lakhs,
        carpet_area_sqft: v.carpet_area_sqft,
      }));
      const { data } = await api.post("/listings/builder-url/commit", {
        url: preview.source_url,
        consent_confirmed: true,
        items,
        project_name: ex.project_name,
        developer_name: ex.developer_name,
        external_rera_number: ex.external_rera_number,
        locality: ex.locality,
        construction_status: ex.construction_status,
      });
      toast.success(`Imported ${data.inserted_count} draft listing(s). Publish from the My Listings tab.`);
      setPreview(null); setUrl(""); setConsent(false);
      onImported && onImported();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed.");
    } finally {
      setCommitting(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="rounded-3xl bg-sg-navy text-white p-7 font-mono text-sm" data-testid="builder-url-input-panel">
        <div className="overline text-sg-gold mb-3">BUILDER · PUBLIC URL</div>
        <p className="text-white/75 text-xs mb-4 leading-relaxed">
          Paste a public builder / project page URL. INPropPortal will check <code className="bg-white/10 px-1 rounded">robots.txt</code> for you, fetch the page under compliant conditions, and use Claude Sonnet 4.5 to extract project info + variants.
        </p>
        <Input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://builder.com/project-name"
          className="bg-transparent border-white/20 text-white font-mono placeholder:text-white/30 rounded-full h-11"
          data-testid="builder-url-input"
        />
        <Button
          onClick={doPreview}
          disabled={loading}
          className="mt-4 w-full rounded-full bg-sg-gold hover:bg-sg-gold-soft text-sg-navy h-11"
          data-testid="builder-url-preview-btn"
        >
          <Sparkles className="w-4 h-4 mr-2" />
          {loading ? "Compliance gate + LLM extraction…" : "Preview via Compliance Gate"}
        </Button>
        {error && (
          <div className={`mt-4 rounded-xl p-3 text-xs ${error.code === 451 ? "bg-red-500/15 text-red-100 border border-red-500/40" : "bg-amber-500/15 text-amber-100 border border-amber-500/40"}`} data-testid="builder-url-error">
            <div className="flex items-center gap-2 mb-1">
              <ShieldOff className="w-3.5 h-3.5" />
              <span className="font-semibold">{error.code === 451 ? "COMPLIANCE BLOCK" : "FETCH FAILED"}</span>
            </div>
            <div>{error.detail}</div>
          </div>
        )}
      </div>

      <div className="rounded-3xl bg-white border border-sg-line p-7" data-testid="builder-url-preview-panel">
        <div className="overline text-sg-gold-deep mb-3">EXTRACTED · READY TO IMPORT</div>
        {!preview ? (
          <div className="text-sm text-sg-mute py-12 text-center">
            Awaiting a compliant URL preview…
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <Badge className="rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                <ShieldCheck className="w-3 h-3 mr-1" /> {preview.compliance?.decision || "allowed"} · {preview.compliance?.gate || "robots.txt"}
              </Badge>
              {preview.extracted?.construction_status && (
                <Badge className="rounded-full bg-sg-gold/20 text-sg-gold-deep border border-sg-gold/40">
                  {preview.extracted.construction_status.replace(/_/g, " ")}
                </Badge>
              )}
            </div>
            <div className="space-y-1.5 text-sm">
              <Row k="Project" v={preview.extracted?.project_name} />
              <Row k="Developer" v={preview.extracted?.developer_name} />
              <Row k="Locality" v={preview.extracted?.locality} />
              <Row k="RERA" v={preview.extracted?.external_rera_number} mono />
              <Row k="Starting BHK" v={preview.extracted?.starting_bhk} />
              <Row k="Starting price" v={preview.extracted?.starting_price_lakhs ? `₹ ${preview.extracted.starting_price_lakhs} Lac` : "—"} />
            </div>

            <div className="mt-5">
              <div className="overline text-sg-gold-deep mb-2">VARIANTS · {preview.variants?.length || 0}</div>
              {(!preview.variants || preview.variants.length === 0) ? (
                <div className="text-xs text-sg-mute italic">No price cells detected in this page.</div>
              ) : (
                <div className="max-h-56 overflow-auto rounded-xl border border-sg-line divide-y divide-sg-line" data-testid="builder-url-variants">
                  {preview.variants.map((v, i) => (
                    <div key={i} className="flex items-center justify-between px-3 py-2 text-sm">
                      <span className="text-sg-navy">
                        {v.bhk || "—"}{v.unit_variant ? ` · ${v.unit_variant}` : ""}
                      </span>
                      <span className="font-mono text-sg-navy">₹ {v.price_lakhs || "—"} Lac</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-5 border-t border-dashed border-sg-line pt-4 flex flex-col gap-3">
              <label className="flex items-start gap-3 text-sm cursor-pointer">
                <Checkbox
                  id="builder-url-consent"
                  checked={consent}
                  onCheckedChange={(v) => setConsent(!!v)}
                  data-testid="builder-url-consent"
                />
                <span>
                  I confirm I have owner / listing rights, this URL was fetched under compliant conditions, and no unauthorised PII is present.
                </span>
              </label>
              <Button
                onClick={doCommit}
                disabled={!consent || committing || !preview.variants || preview.variants.length === 0}
                className="rounded-full bg-sg-navy hover:bg-sg-navy-700 text-white"
                data-testid="builder-url-commit-btn"
              >
                <LinkIcon className="w-4 h-4 mr-1 text-sg-gold" />
                {committing ? "Importing…" : `Import ${preview.variants?.length || 0} draft listing(s)`}
              </Button>
              <p className="text-xs text-sg-mute italic">{preview.disclaimer}</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Row({ k, v, mono }) {
  return (
    <div className="flex justify-between border-b border-dashed border-sg-line py-1.5">
      <span className="text-sg-mute">{k}</span>
      <span className={`text-sg-navy ${mono ? "font-mono" : "font-medium"}`}>{v || "—"}</span>
    </div>
  );
}
